/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type { ShopperProducts } from '@/scapi';

type Category = ShopperProducts.schemas['Category'];

/** The subnav a preview holds open: a level 1, plus the drawer on a featured one. */
export type NavPreviewTarget = { categoryId: string; drawerId?: string };

/**
 * Where a tile for `id` shows: a regular level 1 that has a panel, or a level 2 drawer of a
 * featured level 1. A featured level 1 itself, a group, or an unknown id has no tile slot: null.
 */
export function resolveNavPreviewTarget(root: Category | undefined, id: string): NavPreviewTarget | null {
    for (const top of root?.categories ?? []) {
        const children = top.categories ?? [];
        if (top.c_featured === true) {
            const drawer = children.find((child) => child.id === id);
            if (drawer) return { categoryId: top.id, drawerId: drawer.id };
        } else if (top.id === id && children.length > 0) {
            return { categoryId: top.id };
        }
    }
    return null;
}

/** The ids a mobile sheet drills through to reach the same slot. */
export function navPreviewPath(target: NavPreviewTarget): string[] {
    return target.drawerId ? [target.categoryId, target.drawerId] : [target.categoryId];
}
